import React from 'react';
import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
  Font,
} from '@react-pdf/renderer';

import MonaSansRegular from '@repo/common/assets/fonts/en/MonaSans-Regular.ttf';
import MonaSansMedium from '@repo/common/assets/fonts/en/MonaSans-Medium.ttf';

import NotoSansKRRegular from '@repo/common/assets/fonts/kr/NotoSansKR-Regular.ttf';
import NotoSansKRMedium from '@repo/common/assets/fonts/kr/NotoSansKR-Medium.ttf';

import {
  formatDate,
  formatBirthdayWithAge,
  getLocationCountry,
  hasValue,
} from '@repo/common';

Font.register({
  family: 'MonaSans',
  fonts: [
    { src: MonaSansRegular, fontWeight: 'normal' },
    { src: MonaSansMedium, fontWeight: 500 },
    { src: NotoSansKRRegular, fontWeight: 'normal' },
    { src: NotoSansKRMedium, fontWeight: 500 },
  ],
});

Font.register({
  family: 'NotoSansKR',
  fonts: [
    { src: NotoSansKRRegular, fontWeight: 'normal' },
    { src: NotoSansKRMedium, fontWeight: 500 },
  ],
});

const styles = StyleSheet.create({
  page: {
    width: 595,
    height: 850,
    paddingTop: 24,
    paddingBottom: 20,
    paddingHorizontal: 30,
    fontFamily: 'MonaSans',
    fontSize: 10,
    color: '#171410',
    backgroundColor: '#ffffff',
  },

  noBreak: {
    breakInside: 'avoid',
  },

  breakBefore: {
    breakBefore: 'page',
  },

  headerRight: {
    textAlign: 'right',
    fontSize: 14,
    fontWeight: 500,
    letterSpacing: 2.2,
    marginBottom: 4,
  },

  main: {
    flexDirection: 'row',
  },

  left: {
    width: 350,
    paddingRight: 23,
  },

  right: {
    flex: 1,
  },

  koreanName: {
    fontSize: 14,
    fontWeight: 'normal',
    marginBottom: 2,
  },

  englishName: {
    fontSize: 18,
    fontWeight: 500,
    marginBottom: 4,
  },

  locationBadge: {
    fontSize: 9,
    backgroundColor: '#f9f7f4',
    color: '#686561',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    marginRight: 4,
    marginTop: 4,
    marginBottom: 12,
  },

  sectionTitle: {
    fontSize: 14,
    fontWeight: 500,
    letterSpacing: 2.6,
    marginBottom: 4,
    marginTop: 8,
  },

  sectionWrapper: {
    position: 'relative',
    alignSelf: 'flex-start',
    marginBottom: 6,
  },

  sectionHighlight: {
    position: 'absolute',
    bottom: 2,
    left: 0,
    right: 0,
    height: '36%',
    backgroundColor: '#1DC37B',
  },

  sectionText: {
    fontSize: 14,
    fontWeight: 500,
    letterSpacing: 0.14,
    paddingVertical: 2,
  },

  text10: {
    fontSize: 10,
    lineHeight: 1.4,
  },

  text9: {
    fontSize: 9,
    lineHeight: 1.4,
  },

  text85: {
    fontSize: 8.5,
    lineHeight: 1.4,
    letterSpacing: -0.255,
  },

  visaBox: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    backgroundColor: '#f9f7f4',
    paddingHorizontal: 16,
    paddingVertical: 6,
    borderRadius: 8,
    marginTop: 8,
    breakInside: 'avoid',
  },

  visaBadge: {
    backgroundColor: '#1dc37b',
    color: '#c6f9e9',
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 6,
    fontSize: 10,
  },

  visaTypeText: {
    fontSize: 9,
    color: '#686561',
    marginTop: 4,
  },

  summaryBorder: {
    borderLeftWidth: 1,
    borderLeftColor: '#121212',
    paddingLeft: 10,
  },

  careerHeaderWrapper: {
    alignSelf: 'flex-end',
    paddingBottom: 10,
  },

  careerTextWrapper: {
    alignSelf: 'flex-start',
    paddingLeft: 60,
    paddingBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#121212',
  },

  careerText: {
    fontSize: 14,
    fontWeight: 500,
    letterSpacing: 2.2,
    textAlign: 'right',
  },

  dividerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 2,
  },

  dividerLeft: {
    width: 76,
    borderBottomWidth: 1,
    borderBottomColor: '#121212',
    marginRight: 20,
  },

  dividerRight: {
    flex: 1,
    borderBottomWidth: 1,
    borderBottomColor: '#0EA76D',
  },

  timelineRow: {
    flexDirection: 'row',
    marginBottom: 16,
  },

  timelineLeft: {
    width: 76,
    marginRight: 20,
  },

  timelineRight: {
    flex: 1,
  },

  headlineWrapper: {
    position: 'relative',
    alignSelf: 'flex-start',
  },

  headlineHighlight: {
    position: 'absolute',
    bottom: 2,
    left: 0,
    right: 0,
    height: '40%',
    backgroundColor: '#1DC37B',
  },

  headline: {
    fontSize: 10,
    lineHeight: 1.4,
    paddingVertical: 2,
  },

  bulletRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },

  bulletDot: {
    width: 8,
    fontSize: 8.5,
  },

  bulletText: {
    flex: 1,
    fontSize: 8.5,
    lineHeight: 1.4,
  },

  profileImage: {
    width: 180,
    height: 220,
    marginBottom: 28,
    backgroundColor: '#E5E5E5',
  },
});

const hasKorean = (text = '') => /[ㄱ-ㅎ|ㅏ-ㅣ|가-힣]/.test(text);

const AutoText = ({ children, style }) => {
  const stringify = (val) => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'string') return val;
    if (typeof val === 'number') return String(val);
    if (Array.isArray(val)) return val.map(stringify).join('');
    if (typeof val === 'object') return '';
    return String(val);
  };

  const text = stringify(children);
  const styleArray = Array.isArray(style) ? style : [style];

  return (
    <Text
      style={[
        ...styleArray,
        { fontFamily: hasKorean(text) ? 'NotoSansKR' : 'MonaSans' },
      ]}
    >
      {text}
    </Text>
  );
};

const splitLines = (text) =>
  text
    ?.split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => (l.startsWith('-') ? l.replace(/^-+\s*/, '') : l)) ?? [];

const ResumePDFDocument = ({ resumeData, lang }) => {
  const {
    profile,
    summary,
    visa,
    experience,
    activity,
    education,
    skill,
    language,
    link,
  } = resumeData;

  const koreanName = Array.isArray(profile?.koreanName)
    ? profile.koreanName.join(' ')
    : profile?.koreanName || '';

  return (
    <Document>
      <Page size={[595, 842]} style={styles.page} wrap>
        {/* HEADER */}
        {profile?.career && (
          <View style={styles.careerHeaderWrapper}>
            <View style={styles.careerTextWrapper}>
              <AutoText style={styles.careerText}>
                {Array.isArray(profile.targetRole) &&
                profile.targetRole.length > 0
                  ? `${profile.targetRole.join(', ')}, ${profile.career}`
                  : profile.career}
              </AutoText>
            </View>
          </View>
        )}

        <View style={styles.main}>
          {/* ================= LEFT ================= */}
          <View style={styles.left}>
            {/* NAME */}
            {profile && (
              <View style={[styles.noBreak]}>
                <AutoText
                  style={[styles.koreanName, { fontFamily: 'NotoSansKR' }]}
                >
                  {koreanName}
                </AutoText>
                <View style={{ position: 'relative' }}>
                  <AutoText style={styles.englishName}>
                    {profile.firstName}, {profile.lastName}
                  </AutoText>

                  <AutoText
                    style={{
                      ...styles.englishName,
                      position: 'absolute',
                      left: 0.5,
                      top: 0,
                    }}
                  >
                    {profile.firstName}, {profile.lastName}
                  </AutoText>
                </View>

                {(profile.location || profile.koreanSkills) && (
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
                    {profile.location && (
                      <AutoText style={styles.locationBadge}>
                        {lang === 'KR'
                          ? `${profile.location} 거주`
                          : `Based in ${getLocationCountry(profile.location)}`}
                      </AutoText>
                    )}
                    {profile.koreanSkills && (
                      <AutoText style={styles.locationBadge}>
                        {profile.topik
                          ? profile.topik.level.split(' (')[0]
                          : profile.koreanSkills}
                      </AutoText>
                    )}
                  </View>
                )}
              </View>
            )}

            {/* PROFILE */}
            {(hasValue(profile?.birthday) ||
              hasValue(profile?.nationality) ||
              hasValue(profile?.phone) ||
              hasValue(profile?.email)) && (
              <View style={styles.noBreak}>
                <View style={styles.sectionWrapper}>
                  <View style={styles.sectionHighlight} />
                  <Text style={styles.sectionText}>Profile</Text>
                </View>
                <View>
                  {profile.birthday && (
                    <AutoText style={styles.text10}>
                      {String(
                        lang === 'KR'
                          ? formatBirthdayWithAge(profile.birthday, true)
                          : formatBirthdayWithAge(profile.birthday),
                      )}
                    </AutoText>
                  )}
                  {hasValue(profile?.nationality) && (
                    <View style={{ flexDirection: 'row' }}>
                      <AutoText
                        style={[
                          styles.text10,
                          lang === 'KR' ? { width: 52 } : {},
                        ]}
                      >
                        {lang === 'KR' ? '국적' : 'Nationality '}
                      </AutoText>
                      <AutoText style={[styles.text10, { flex: 1 }]}>
                        {Array.isArray(profile.nationality)
                          ? profile.nationality.join(', ')
                          : profile.nationality}
                      </AutoText>
                    </View>
                  )}
                  {Array.isArray(profile.phone) && profile.phone.length > 0 && (
                    <View style={{ flexDirection: 'row' }}>
                      <AutoText
                        style={[
                          styles.text10,
                          lang === 'KR' ? { width: 52 } : {},
                        ]}
                      >
                        {lang === 'KR' ? '연락처' : 'Contact '}
                      </AutoText>
                      <AutoText style={[styles.text10, { flex: 1 }]}>
                        {profile.phone
                          .map((p) => `${p.countryCode} ${p.number}`)
                          .join(', ')}
                      </AutoText>
                    </View>
                  )}
                  {hasValue(profile?.email) && (
                    <View style={{ flexDirection: 'row' }}>
                      <AutoText
                        style={[
                          styles.text10,
                          lang === 'KR' ? { width: 52 } : {},
                        ]}
                      >
                        {lang === 'KR' ? '이메일' : 'Email '}
                      </AutoText>
                      <AutoText style={[styles.text10, { flex: 1 }]}>
                        {Array.isArray(profile.email)
                          ? profile.email.join(', ')
                          : profile.email}
                      </AutoText>
                    </View>
                  )}
                </View>
              </View>
            )}

            {/* VISA */}
            {visa?.length > 0 && (
              <View style={{ marginBottom: 4 }}>
                {visa.map((v, i) => (
                  <View key={i} style={[styles.noBreak, styles.visaBox]}>
                    <View style={{ paddingRight: 12, alignItems: 'center' }}>
                      <AutoText style={styles.visaBadge}>{v.visaType}</AutoText>
                      <AutoText style={styles.visaTypeText}>
                        {v.visaTypeText}{' '}
                        {lang?.toUpperCase() === 'KR' ? '비자' : 'visa'}
                      </AutoText>
                    </View>
                    {v.expiryDate && (
                      <View
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          marginLeft: 16,
                        }}
                      >
                        <AutoText style={[styles.text9, { color: '#686561' }]}>
                          {lang?.toUpperCase() === 'KR'
                            ? '만료일'
                            : 'Date of Expiry '}
                        </AutoText>
                        <AutoText style={[styles.text10, { marginLeft: 8 }]}>
                          {formatDate(v.expiryDate)}
                        </AutoText>
                      </View>
                    )}
                  </View>
                ))}
              </View>
            )}

            {/* SUMMARY */}
            {hasValue(summary) && (
              <View style={[styles.noBreak, { marginBottom: 8 }]}>
                <Text style={styles.sectionTitle}>SUMMARY</Text>
                <View style={styles.summaryBorder}>
                  <AutoText
                    style={{
                      fontSize: 10,
                      lineHeight: 1.4,
                      fontWeight: 'normal',
                      letterSpacing: 0,
                      color: '#171410',
                      whiteSpace: 'pre-line',
                      breakWords: 'break-word',
                    }}
                  >
                    {summary}
                  </AutoText>
                </View>
              </View>
            )}

            {/* EXPERIENCE */}
            {experience?.length > 0 && (
              <View style={{ marginBottom: 8 }}>
                <Text style={styles.sectionTitle}>WORK EXPERIENCE</Text>
                <View style={styles.dividerRow}>
                  <View style={styles.dividerLeft} />
                  <View style={styles.dividerRight} />
                </View>

                {experience.map((exp, i) => (
                  <View key={i} style={[styles.noBreak, styles.timelineRow]}>
                    <View style={styles.timelineLeft}>
                      {exp.employmentPeriod && (
                        <AutoText style={[styles.text10, { marginBottom: 2 }]}>
                          {exp.employmentPeriod}
                        </AutoText>
                      )}
                      {exp.company && (
                        <AutoText
                          style={[
                            styles.text10,
                            { fontWeight: 500, marginBottom: 2 },
                          ]}
                        >
                          {exp.company}
                        </AutoText>
                      )}
                      {exp.jobTitle && (
                        <AutoText
                          style={[
                            styles.text10,
                            {
                              color: '#666',
                              transform: 'skew(-10deg)',
                              marginBottom: 2,
                            },
                          ]}
                        >
                          {exp.jobTitle}
                        </AutoText>
                      )}
                      {exp.employmentType && (
                        <AutoText style={styles.text10}>
                          {exp.employmentType}
                        </AutoText>
                      )}
                    </View>

                    <View style={styles.timelineRight}>
                      {exp.headline && (
                        <View style={{ position: 'relative' }}>
                          <View style={styles.headlineWrapper}>
                            <View style={styles.headlineHighlight} />
                            <AutoText style={styles.headline}>
                              {exp.headline}
                            </AutoText>
                          </View>
                        </View>
                      )}
                      {exp.keyAchievements?.map((a, j) => (
                        <View key={j} style={{ marginBottom: 4 }}>
                          {a.title && (
                            <AutoText
                              style={[styles.text85, { fontWeight: 500 }]}
                            >
                              {a.title}
                            </AutoText>
                          )}
                          {splitLines(a.description).map((line, k) => (
                            <View key={k} style={styles.bulletRow}>
                              <AutoText style={styles.bulletDot}>•</AutoText>
                              <AutoText style={styles.bulletText}>
                                {line}
                              </AutoText>
                            </View>
                          ))}
                        </View>
                      ))}
                    </View>
                  </View>
                ))}
              </View>
            )}

            {/* ACTIVITIES */}
            {activity?.length > 0 && (
              <View style={{ marginBottom: 16 }}>
                <Text style={styles.sectionTitle}>ACTIVITIES</Text>
                <View style={styles.dividerRow}>
                  <View style={styles.dividerLeft} />
                  <View style={styles.dividerRight} />
                </View>
                {activity.map((act, i) => (
                  <View key={i} style={[styles.noBreak, styles.timelineRow]}>
                    <View style={styles.timelineLeft}>
                      <Text style={styles.text10}>{act.date}</Text>
                    </View>
                    <View style={styles.timelineRight}>
                      <AutoText style={[styles.text10, { fontWeight: 500 }]}>
                        {act.title}
                      </AutoText>
                      {splitLines(act.description).map((line, k) => (
                        <View key={k} style={styles.bulletRow}>
                          <AutoText style={styles.bulletDot}>•</AutoText>
                          <AutoText style={styles.bulletText}>{line}</AutoText>
                        </View>
                      ))}
                    </View>
                  </View>
                ))}
              </View>
            )}
          </View>

          {/* ================= RIGHT ================= */}
          <View style={styles.right}>
            {/* IMAGE */}
            {profile?.profileUrl && (
              <Image src={profile.profileUrl} style={styles.profileImage} />
            )}

            {/* EDUCATION */}
            {education?.length > 0 && (
              <View>
                <Text style={styles.sectionTitle}>EDUCATION</Text>
                {education.map((e) => (
                  <View
                    key={e.id}
                    style={[styles.noBreak, { marginBottom: 8 }]}
                  >
                    <Text style={[styles.text10, { marginBottom: 4 }]}>
                      {e.degree}, {e.fieldOfStudy}
                    </Text>
                    <Text style={[styles.text10, { marginBottom: 4 }]}>
                      {e.schoolName}
                    </Text>
                    <Text style={[styles.text10, { marginBottom: 4 }]}>
                      {e.studyPeriod}
                    </Text>
                  </View>
                ))}
              </View>
            )}

            {/* SKILLS */}
            {(language?.length > 0 || skill?.length > 0) && (
              <View style={{ marginBottom: 16 }}>
                <Text style={styles.sectionTitle}>SKILLS</Text>
                {language?.length > 0 && (
                  <View style={{ marginBottom: 8 }}>
                    <Text style={[styles.text10, { marginBottom: 6 }]}>
                      •{'  '} Languages
                    </Text>
                    {language.map((lang) => (
                      <View
                        key={lang.id}
                        style={[
                          styles.noBreak,
                          { flexDirection: 'row', marginTop: 4 },
                        ]}
                      >
                        <AutoText style={[styles.text10, { minWidth: 50 }]}>
                          {lang.language}
                        </AutoText>
                        <View style={{ marginLeft: 8 }}>
                          <Text style={styles.text10}>
                            {lang.proficiencyLevel}
                          </Text>

                          <View
                            style={{
                              flexDirection: 'row',
                              alignItems: 'center',
                              gap: 6,
                            }}
                          >
                            <Text style={[styles.text10, { color: '#686561' }]}>
                              {lang.test}
                            </Text>

                            <AutoText
                              style={[styles.text10, { color: '#55524E' }]}
                            >
                              {lang.score}
                            </AutoText>

                            <Text style={[styles.text10, { color: '#55524E' }]}>
                              {formatDate(lang.issueDate, {
                                yearMonthOnly: true,
                              })}
                            </Text>
                          </View>
                        </View>
                      </View>
                    ))}
                  </View>
                )}
                {skill?.length > 0 && (
                  <View>
                    <Text style={[styles.text10, { marginBottom: 6 }]}>
                      •{'  '}Technical
                    </Text>
                    {skill.map((s, i) => (
                      <Text
                        key={i}
                        style={[
                          styles.noBreak,
                          styles.text10,
                          { marginBottom: 4 },
                        ]}
                      >
                        {s}
                      </Text>
                    ))}
                  </View>
                )}
              </View>
            )}

            {/* LINKS */}
            {link?.length > 0 && (
              <View>
                <Text style={styles.sectionTitle}>LINKS</Text>
                {link.map((l) => (
                  <Text key={l.id} style={[styles.noBreak, styles.text10]}>
                    •{'  '}
                    {l.link.replace('https://', '')}
                  </Text>
                ))}
              </View>
            )}
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default ResumePDFDocument;
